import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { PlaceForm, type Place } from './RouteEndpoints';

/**
 * 「我的」页里的常用终点(家、车库…)—— 智能推荐路线时挑一个当终点。
 *
 * ★ 为什么这里也要有:规划面板只在手上有单时才出现,
 *   没单的时候(比如晚上想先把「家」存好)就没地方管了。
 *   这里随时能新增、改名、改地址、设默认、删除。
 */
export function MyPlaces() {
  const [places, setPlaces] = useState<Place[] | null>(null);
  /** 正在编辑哪一个;'NEW' = 新增 */
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPlaces(await api<Place[]>('/driver/places'));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setErr(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '操作失败');
    }
  }

  return (
    <section
      aria-label="我的终点"
      className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
    >
      <h2 className="font-semibold text-slate-900 dark:text-slate-100">我的终点</h2>
      <p className="mt-1 text-sm text-slate-500">
        送完最后一单要去的地方(比如家)。智能推荐路线时选它当终点,路线就朝那里排。
      </p>

      <ul className="mt-3 space-y-2">
        {places?.length === 0 && editing !== 'NEW' && (
          <li className="text-sm text-slate-400">还没有存终点</li>
        )}
        {places?.map((p) =>
          editing === p.id ? (
            <li key={p.id}>
              <PlaceForm
                initial={p}
                submitLabel="保存"
                onCancel={() => setEditing(null)}
                onSubmit={async (v) => {
                  await api(`/driver/places/${p.id}`, {
                    method: 'PATCH',
                    body: { name: v.name, address: v.address, lat: v.lat, lng: v.lng },
                  });
                  setEditing(null);
                  await load();
                }}
              />
            </li>
          ) : (
            <li
              key={p.id}
              className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 dark:text-slate-100">
                  {p.name}
                  {p.isDefault && (
                    <span className="ml-1.5 rounded bg-brand-600 px-1.5 py-0.5 text-[10px] text-white">默认</span>
                  )}
                </div>
                <div className="truncate text-xs text-slate-500">{p.address}</div>
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-x-3 gap-y-1 text-xs">
                <button type="button" onClick={() => setEditing(p.id)} className="text-brand-600">
                  编辑
                </button>
                {!p.isDefault && (
                  <button
                    type="button"
                    onClick={() =>
                      void act(() => api(`/driver/places/${p.id}`, { method: 'PATCH', body: { isDefault: true } }))
                    }
                    className="text-brand-600"
                  >
                    设为默认
                  </button>
                )}
                {confirmDel === p.id ? (
                  <button
                    type="button"
                    onClick={() => void act(() => api(`/driver/places/${p.id}`, { method: 'DELETE' }))}
                    className="text-red-600"
                  >
                    确认删除
                  </button>
                ) : (
                  <button type="button" onClick={() => setConfirmDel(p.id)} className="text-slate-400">
                    删除
                  </button>
                )}
              </div>
            </li>
          ),
        )}
      </ul>

      {editing === 'NEW' ? (
        <div className="mt-2">
          <PlaceForm
            submitLabel="保存"
            onCancel={() => setEditing(null)}
            onSubmit={async (v) => {
              await api('/driver/places', { method: 'POST', body: v });
              setEditing(null);
              await load();
            }}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing('NEW')}
          className="mt-2 w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-brand-600
                     dark:border-slate-600"
        >
          + 新增终点
        </button>
      )}
      {err && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{err}</p>}
    </section>
  );
}
